import { FC, useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import {
    VStack,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    Switch,
} from "@chakra-ui/react";
import {
    Company,
    CompanyApi,
    ServiceApi,
    ServiceGroup,
    ServiceGroupApi,
    Training,
    TrainingApi,
    Typology,
    TypologyApi,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { AppFilterContainer } from "../../../containers";
import {
    AppFormControl,
    AppIcon,
    AppSelect,
    AppSelectOption,
    AppDatePicker,
} from "../../../components";
import { DateRangeType } from "../MyInternship/MyInternshipFilter";
import { APPC, UTIL } from "../../../config";

export type FindInternshipCompanyFilterDataType = {
    name: string;
    training: string;
    typology: string[];
    company: string;
    service: string[];
    weeks: number | null;
    startDateRange: DateRangeType;
    includeAvailableSeats: boolean;
};

const defaultValues: FindInternshipCompanyFilterDataType = {
    name: "",
    training: "",
    typology: [],
    company: "",
    service: [],
    weeks: null,
    startDateRange: [null, null],
    includeAvailableSeats: false,
};

export const FindInternshipCompanyFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();

    // state & const
    const [isInit, setIsInit] = useState(false);
    const [loadingTraining, setLoadingTraining] = useState(true);
    const [trainingOptions, setTrainingOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTraining, setSelectedTraining] = useState<AppSelectOption>();
    const [loadingTypology, setLoadingTypology] = useState(true);
    const [typologyOptions, setTypologyOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTypologies, setSelectedTypologies] = useState<
        AppSelectOption[]
    >([]);
    const [loadingCompany, setLoadingCompany] = useState(true);
    const [companyOptions, setCompanyOptions] = useState<AppSelectOption[]>([]);
    const [selectedCompany, setSelectedCompany] =
        useState<AppSelectOption | null>(null);
    const [loadingService, setLoadingService] = useState(false);
    const [serviceGroupedOptions, setServiceGroupedOptions] = useState<
        { label: string; options: AppSelectOption[] }[]
    >([]);
    const [selectedServices, setSelectedServices] = useState<AppSelectOption[]>(
        []
    );
    const dayjs = UTIL.getDayJs();

    const { handleSubmit, reset, setValue, control } =
        useForm<FindInternshipCompanyFilterDataType>({
            mode: "onSubmit",
        });

    // handler
    const onSubmitHandler = (formData: FindInternshipCompanyFilterDataType) => {
        const filterParams: Record<string, any> = {};
        Object.keys(formData).forEach((key) => {
            if (key === "startDateRange" || key === "weeks") {
                return;
            }

            if (formData[key]) {
                let nKey = key;
                switch (key) {
                    case "company":
                    case "service":
                    case "training":
                    case "typology":
                        nKey = `internship.${key}.id`;
                        break;
                }
                if (
                    (key === "typology" && formData["typology"].length > 0) ||
                    key === "company" ||
                    key === "training" ||
                    (key === "service" && formData["service"].length > 0) ||
                    key === "includeAvailableSeats" ||
                    (key !== "typology" && key !== "service")
                ) {
                    filterParams[nKey] = formData[key];
                }
            }
        });
        // startDateRange transformation
        if (formData.startDateRange[0]) {
            filterParams["internshipSchedule.startDate[after]"] = dayjs(
                formData.startDateRange[0]
            ).format(APPC.APP_HTML_DATE_FORMAT);
        }
        if (formData.startDateRange[1]) {
            filterParams["internshipSchedule.startDate[before]"] = dayjs(
                formData.startDateRange[1]
            ).format(APPC.APP_HTML_DATE_FORMAT);
        }
        if (formData.weeks) {
            filterParams["internshipSchedule.weeks[gte]"] = formData.weeks;
            filterParams["internshipSchedule.weeks[lt]"] = formData.weeks + 1;
        }
        setFilterData(filterParams);
    };

    const loadTrainingOptions = () => {
        setLoadingTraining(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            "training.id": `${selectedTraining?.value}`,
        };

        TrainingApi.getCollectionForSelect<Training>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((training) => ({
                        value: TrainingApi.toResourceIRI(training.id),
                        label: training.name,
                    }));
                    setTrainingOptions(options);
                }
            })
            .finally(() => setLoadingTraining(false));
    };

    const loadTypologyOptions = () => {
        setLoadingTypology(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            "training.id": `${selectedTraining?.value}`,
        };

        TypologyApi.getCollectionForSelect<Typology>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((typology) => ({
                        value: TypologyApi.toResourceIRI(typology.id),
                        label: typology.name,
                    }));
                    setTypologyOptions(options);
                }
            })
            .finally(() => setLoadingTypology(false));
    };

    const loadMyCompanyOptions = () => {
        setLoadingCompany(true);
        const params = {
            pagination: false,
            "order[name]": "asc",
        };

        CompanyApi.getCollectionMyCompanies<Company>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((company) => ({
                        value: CompanyApi.toResourceIRI(company.id),
                        label: company.name,
                    }));
                    setCompanyOptions(options);
                }
            })
            .finally(() => setLoadingCompany(false));
    };

    const loadServiceOptions = () => {
        if (selectedCompany && selectedCompany.value) {
            const params = {
                [`order[name]`]: "asc",
                ["company.id"]: ServiceGroupApi.resourceIRItoId(
                    selectedCompany.value as string
                ),
                pagination: false,
            };
            setLoadingService(true);
            ServiceGroupApi.getCollection<ServiceGroup>(1, params)
                .then(({ response }) => {
                    if (response !== null) {
                        const options = response.items.map((group) => ({
                            label: group.name,
                            options: (group.services || []).map((service) => ({
                                value: ServiceApi.toResourceIRI(service.id),
                                label: service.name,
                            })),
                        }));
                        setServiceGroupedOptions(options);
                    }
                })
                .finally(() => setLoadingService(false));
        }
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedTraining(undefined);
        setSelectedTypologies([]);
        setSelectedCompany(null);
        setServiceGroupedOptions([]);
        setSelectedServices([]);
        reset(defaultValues);
    };

    // useEffect
    useEffect(() => {
        if (isInit) {
            loadTrainingOptions();
            loadMyCompanyOptions();
        }
    }, [isInit]);

    useEffect(() => {
        if (selectedTraining) {
            loadTypologyOptions();
        }
    }, [selectedTraining]);

    useEffect(() => {
        loadServiceOptions();
    }, [selectedCompany]);

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            setIsInit={setIsInit}
            zIndex={"1400"}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"name"}
                            defaultValue={filterData?.name || ""}
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:training.label")}>
                        <Controller
                            control={control}
                            name="training"
                            defaultValue={selectedTraining?.value as string}
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingTraining}
                                    options={trainingOptions}
                                    value={selectedTraining || null}
                                    placeholder={t(
                                        "ent.Internship:training.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedTraining(
                                            e as AppSelectOption
                                        );
                                        setSelectedTypologies([]);
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:typology.label")}>
                        <Controller
                            control={control}
                            name="typology"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption, true>
                                    name={field.name}
                                    isClearable
                                    isMulti={true}
                                    isLoading={loadingTypology}
                                    options={typologyOptions}
                                    value={selectedTypologies || null}
                                    placeholder={t(
                                        "ent.Internship:typology.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(
                                            e?.map((option) => option?.value) ||
                                                []
                                        );
                                        setSelectedTypologies(
                                            e as AppSelectOption[]
                                        );
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:company.label")}>
                        <Controller
                            control={control}
                            name="company"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingCompany}
                                    options={companyOptions}
                                    value={selectedCompany || null}
                                    placeholder={t(
                                        "ent.Internship:company.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedCompany(
                                            e as AppSelectOption
                                        );
                                        setServiceGroupedOptions([]);
                                        setSelectedServices([]);
                                        setValue("service", []);
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:service.label")}>
                        <Controller
                            control={control}
                            name="service"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption, true>
                                    name={field.name}
                                    isClearable
                                    isMulti={true}
                                    isLoading={loadingService}
                                    options={serviceGroupedOptions}
                                    value={selectedServices || null}
                                    placeholder={t(
                                        "ent.Internship:service.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(
                                            e?.map((option) => option?.value) ||
                                                []
                                        );
                                        setSelectedServices(
                                            e as AppSelectOption[]
                                        );
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <SimpleGrid
                        columns={{ sm: 1, md: 2 }}
                        spacingX={5}
                        spacingY={4}
                        w={"full"}
                    >
                        <AppFormControl
                            label={t(
                                "padm.MyInternshipPage:startDateRange.label"
                            )}
                        >
                            <Controller
                                control={control}
                                name="startDateRange"
                                defaultValue={[
                                    filterData[
                                        "internshipSchedule.startDate[after]"
                                    ],
                                    filterData[
                                        "internshipSchedule.startDate[before]"
                                    ],
                                ]}
                                render={({ field }) => (
                                    <AppDatePicker
                                        selectsRange={true}
                                        startDate={field.value?.[0] ?? null}
                                        endDate={field.value?.[1] ?? null}
                                        onChange={(update) =>
                                            field.onChange(update)
                                        }
                                        placeholderText={t(
                                            "padm.MyInternshipPage:startDateRange.placeholder"
                                        )}
                                        dateFormat={APPC.APP_DP_DATE_FORMAT}
                                        customInput={<Input />}
                                        isClearable={true}
                                    />
                                )}
                            />
                        </AppFormControl>
                        <AppFormControl
                            label={t("ent.InternshipSchedule:weeks.label")}
                        >
                            <Controller
                                control={control}
                                name="weeks"
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isSearchable={false}
                                        // isClearable={true}
                                        options={[...Array(15)].map(
                                            (_, index) => ({
                                                value: index + 1,
                                                label: String(index + 1),
                                            })
                                        )}
                                        placeholder={t(
                                            "ent.InternshipSchedule:weeks.placeholder"
                                        )}
                                        value={
                                            field.value
                                                ? {
                                                      value: field.value,
                                                      label: String(
                                                          field.value
                                                      ),
                                                  }
                                                : null
                                        }
                                        onChange={(e) => {
                                            field.onChange(e ? e.value : null);
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                    </SimpleGrid>
                    <SimpleGrid
                        columns={{ sm: 1, md: 2 }}
                        gridTemplateColumns={{ sm: "1fr 1fr", md: "6fr 4fr" }}
                        spacingX={5}
                        spacingY={4}
                        w={"full"}
                    >
                        <AppFormControl
                            label={t(
                                "padm.FindInternshipCompanyFilter:includeAvailableSeats.label"
                            )}
                        >
                            <Controller
                                control={control}
                                name="includeAvailableSeats"
                                defaultValue={filterData.includeAvailableSeats}
                                render={({ field }) => (
                                    <Switch
                                        isChecked={field.value}
                                        onChange={(e) =>
                                            field.onChange(e.target.checked)
                                        }
                                    />
                                )}
                            />
                        </AppFormControl>
                    </SimpleGrid>
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
